import React from 'react';
import { useSignupStore } from '@/stores/signupStore';
import { validateBorrowerForm, BorrowerFormData } from '@/utils/formValidation/borrowerForm';
import { ErrorDisplay } from '../ErrorDisplay';
import { DatePicker } from '../ui/DatePicker';
import { PasswordField } from '../ui/PasswordField';
import { PhoneNumberField } from '../ui/PhoneNumberField';
import { TextField } from '../ui/TextField';
import { SignupStepLayout } from './SignupStepLayout';

type BorrowerFormProps = {
  onBack: () => void;
  onNext: () => void;
  onSwitchToLogin?: () => void;
};

const BorrowerForm: React.FC<BorrowerFormProps> = ({ onBack, onNext, onSwitchToLogin }) => {
  const signupStore = useSignupStore();
  const { formData, errors, submitAttempted } = signupStore.borrowerForm;
  const { updateBorrowerFormField, setBorrowerErrors, setBorrowerSubmitAttempted } = signupStore;
  const handleFieldChange = (field: keyof BorrowerFormData, value: string | boolean) => {
    updateBorrowerFormField(field, value);
  };

  const handleSubmit = () => {
    setBorrowerSubmitAttempted(true);

    // Validate form
    const validation = validateBorrowerForm(formData, true);
    if (!validation.isValid) {
      setBorrowerErrors(validation.errors);
      return;
    }

    setBorrowerErrors({});

    onNext();
  };

  const getFieldError = (field: keyof BorrowerFormData): string | undefined => {
    if (!submitAttempted) return undefined;
    return errors[field]?.[0];
  };

  const errorList = Object.values(errors).flat();

  return (
    <SignupStepLayout
      actions={{
        // not sure why is the Sending Verification email... here
        primaryLabel: 'Continue',
        onPrimaryClick: handleSubmit,
        onBack,
      }}
      contentVariant="narrow"
      footer={{
        onSwitchToLogin,
        termsChecked: formData.acceptedTerms,
        onTermsChange: (checked) => handleFieldChange('acceptedTerms', checked),
      }}
    >
      <div className="flex flex-col items-center justify-center gap-8 px-8 py-2">
        <div className="flex flex-col items-center justify-center gap-12">
          <div className="self-stretch flex flex-col items-start justify-start gap-2">
            {/* First & Middle Name side by side */}
            <div className="inline-flex w-[320px] items-start justify-start gap-2">
              <TextField
                value={formData.firstName}
                onChange={(value) => handleFieldChange('firstName', value)}
                placeholder="First Name"
                error={!!getFieldError('firstName')}
              />
              <TextField
                value={formData.middleName}
                onChange={(value) => handleFieldChange('middleName', value)}
                placeholder="Middle Name"
                error={!!getFieldError('middleName')}
              />
            </div>
            {/* Lastname */}
            <TextField
              value={formData.lastname}
              onChange={(value) => handleFieldChange('lastname', value)}
              placeholder="Last name"
              error={!!getFieldError('lastname')}
            />
            {/* Date of Birth */}
            <DatePicker
              value={formData.dateOfBirth}
              onChange={(date) => handleFieldChange('dateOfBirth', date)}
              placeholder="Date of Birth"
              label="Date of Birth"
              error={!!getFieldError('dateOfBirth')}
            />
            {/* Email */}
            <TextField
              value={formData.email}
              onChange={(value) => handleFieldChange('email', value)}
              placeholder="Email"
              type="email"
              error={!!getFieldError('email')}
            />
            {/* Phone Number */}
            <PhoneNumberField
              value={formData.phone}
              onChange={(value) => handleFieldChange('phone', value)}
              placeholder="Phone Number"
              error={!!getFieldError('phone')}
            />
            {/* Password */}
            <PasswordField
              value={formData.password}
              onChange={(value) => handleFieldChange('password', value)}
              placeholder="Password"
              error={!!getFieldError('password')}
            />
            {/* Confirm Password */}
            <PasswordField
              value={formData.repeatPassword}
              onChange={(value) => handleFieldChange('repeatPassword', value)}
              placeholder="Confirm Password"
              error={!!getFieldError('repeatPassword')}
            />
            {/* Error list */}
            <ErrorDisplay errors={errorList} />
          </div>
        </div>
      </div>
    </SignupStepLayout>
  );
};

export default BorrowerForm;
